import React, { useState, useRef, useEffect } from 'react';
import { Paper, TextField, Button, Typography, Box, CircularProgress, IconButton, Divider, Chip } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import DownloadIcon from '@mui/icons-material/Download';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useAuth } from '../contexts/AuthContext';
import authService from '../services/auth.service';

interface Message {
    id: string;
    type: 'user' | 'assistant';
    content: string;
    timestamp: Date;
    isReport?: boolean;
}

interface SuggestedQuery {
    text: string;
    icon: string;
}

const AIAssistant: React.FC = () => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const { user } = useAuth();

    const suggestedQueries: SuggestedQuery[] = [
        { text: "How many invoices were submitted on January 3rd?", icon: "📄" },
        { text: "Show me all images processed this week", icon: "🖼️" },
        { text: "List purchases from Excel files", icon: "📊" },
        { text: "Generate a monthly summary report", icon: "📈" }
    ];

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const handleSuggestedQuery = (query: string) => {
        setInput(query);
        handleSendMessage(query);
    };

    const handleSendMessage = async (messageText?: string) => {
        const text = messageText || input;
        if (!text.trim()) return;

        const userMessage: Message = {
            id: Date.now().toString(),
            type: 'user',
            content: text,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setIsLoading(true);
        setError(null);

        try {
            const api = authService.getApiInstance();
            const response = await api.post('/ai-query', {
                query: text,
                userId: user?._id
            });

            const assistantMessage: Message = {
                id: (Date.now() + 1).toString(),
                type: 'assistant',
                content: response.data.response,
                timestamp: new Date(),
                isReport: response.data.isReport
            };

            setMessages(prev => [...prev, assistantMessage]);
        } catch (err: any) {
            console.error('AI Query Error:', err);
            
            // Check if it's an authentication error
            if (err.response?.status === 401) {
                setError('Your session has expired. Please refresh the page to continue.');
            } else {
                setError(err.response?.data?.error || 'Failed to get response from AI assistant');
            }
            
            const errorMessage: Message = {
                id: (Date.now() + 1).toString(),
                type: 'assistant',
                content: "I'm sorry, I encountered an error processing your request. Please try again.",
                timestamp: new Date()
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleDownloadReport = (content: string) => {
        const blob = new Blob([content], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `report-${new Date().toISOString().split('T')[0]}.md`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleReset = () => {
        setMessages([]);
        setInput('');
        setError(null);
    };

    return (
        <div className="flex flex-col h-full max-h-[calc(100vh-64px)]">
            <Box className="bg-white border-b px-6 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <Typography variant="h5" className="font-semibold text-gray-900">
                            AI Assistant
                        </Typography>
                        <Typography variant="body2" className="text-gray-600 mt-1">
                            Ask questions about your documents and data
                        </Typography>
                    </div>
                    <IconButton onClick={handleReset} title="Reset conversation">
                        <RestartAltIcon />
                    </IconButton>
                </div>
            </Box>

            <Box className="flex-1 overflow-y-auto bg-gray-50 p-6">
                {messages.length === 0 ? (
                    <div className="max-w-4xl mx-auto">
                        <Paper className="p-6 mb-6">
                            <Typography variant="h6" className="mb-4 text-gray-800">
                                Welcome to AI Assistant! I can help you:
                            </Typography>
                            <ul className="space-y-2 text-gray-600 mb-6">
                                <li>• Search and analyze your uploaded documents</li>
                                <li>• Find specific information across all file types</li>
                                <li>• Generate reports and summaries</li>
                                <li>• Answer questions about your data</li>
                            </ul>
                            <Typography variant="body2" className="text-gray-500 mb-4">
                                Try one of these queries to get started:
                            </Typography>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {suggestedQueries.map((query, index) => (
                                    <Button
                                        key={index}
                                        variant="outlined"
                                        onClick={() => handleSuggestedQuery(query.text)}
                                        className="justify-start text-left normal-case"
                                        startIcon={<span>{query.icon}</span>}
                                    >
                                        {query.text}
                                    </Button>
                                ))}
                            </div>
                        </Paper>
                    </div>
                ) : (
                    <div className="max-w-4xl mx-auto space-y-4">
                        {messages.map((message) => (
                            <div
                                key={message.id}
                                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                            >
                                <Paper
                                    className={`p-4 max-w-[80%] ${
                                        message.type === 'user'
                                            ? 'bg-blue-50 ml-auto'
                                            : 'bg-white'
                                    }`}
                                    elevation={1}
                                >
                                    <div className="flex items-start space-x-3">
                                        <div className="flex-1">
                                            <Typography
                                                variant="caption"
                                                className={`block mb-1 ${
                                                    message.type === 'user' ? 'text-blue-700' : 'text-gray-500'
                                                }`}
                                            >
                                                {message.type === 'user' ? 'You' : 'AI Assistant'}
                                            </Typography>
                                            <Typography variant="body1" className="whitespace-pre-wrap">
                                                {message.content}
                                            </Typography>
                                            {message.isReport && (
                                                <Box className="mt-3">
                                                    <Divider className="mb-2" />
                                                    <Button
                                                        size="small"
                                                        startIcon={<DownloadIcon />}
                                                        onClick={() => handleDownloadReport(message.content)}
                                                    >
                                                        Download Report
                                                    </Button>
                                                </Box>
                                            )}
                                        </div>
                                    </div>
                                </Paper>
                            </div>
                        ))}
                        {isLoading && (
                            <div className="flex justify-start">
                                <Paper className="p-4 bg-white" elevation={1}>
                                    <div className="flex items-center space-x-3">
                                        <CircularProgress size={20} />
                                        <Typography variant="body2" className="text-gray-500">
                                            AI is thinking...
                                        </Typography>
                                    </div>
                                </Paper>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                )}
            </Box>

            <Box className="bg-white border-t p-4">
                {error && (
                    <Typography variant="body2" color="error" className="mb-2 text-center">
                        {error}
                    </Typography>
                )}
                <div className="max-w-4xl mx-auto">
                    <div className="flex space-x-3">
                        <TextField
                            fullWidth
                            multiline
                            maxRows={4}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Ask about your documents, request reports, or search for specific information..."
                            variant="outlined"
                            disabled={isLoading}
                            className="bg-gray-50"
                        />
                        <Button
                            variant="contained"
                            onClick={() => handleSendMessage()}
                            disabled={!input.trim() || isLoading}
                            className="min-w-[100px]"
                            endIcon={<SendIcon />}
                        >
                            Send
                        </Button>
                    </div>
                    <Typography variant="caption" className="text-gray-500 mt-2 block text-center">
                        Press Enter to send, Shift+Enter for new line
                    </Typography>
                </div>
            </Box>
        </div>
    );
};

export default AIAssistant;